import { RouteType, RouteParamsType, RouteQueryParamsType } from "types/RouteType";
import DevOpsProjectStore from "./DevOpsProjectStore";
import { devopsLinkDialog } from "../shared/devopsLinkDialog";

/** The route's identity params as strings, or null when either is missing. */
const identityOf = (params: RouteParamsType): { connectionId: string; projectId: string } | null => {
  const connectionId = typeof params.connectionId === "string" ? params.connectionId : "";
  const projectId = typeof params.projectId === "string" ? params.projectId : "";
  return connectionId && projectId ? { connectionId, projectId } : null;
};

/** The open tab — `<Tabs useRouterState>` writes it to `?tabName=`. */
const tabOf = (queryParams: RouteQueryParamsType | undefined): string | undefined => {
  const tab = queryParams?.tabName;
  return typeof tab === "string" && tab ? tab : undefined;
};

/**
 * Project 360 — one Azure DevOps project on one page: its clients, modules,
 * links, webhooks and recent activity, plus the four boards as tabs.
 *
 * IDENTITY is `:connectionId/:projectId` (the overview's project key split
 * in two); the page is reached from the Overview's cards and the boards'
 * chips, not from the nav (`hidden`).
 *
 * `:tab?` IS VESTIGIAL, exactly as on Module 360: tabs deep-link through
 * `?tabName=` (the library `<Tabs useRouterState>` writes it); the path
 * segment only lets a pasted `/devops/projects/c/p/pipelines` still resolve.
 * Nothing reads it — do not build logic on it.
 *
 * The shared scope: entering sets `devopsScopeStore` to this project, so the
 * board bodies in the tabs show it without anyone choosing it again. A board
 * tab is primed from the params-change hook (a tab switch fires it), never
 * from a component effect.
 */
const devopsProjectRoute: RouteType[] = [
  {
    name: "DevOpsProject",
    path: "/devops/projects/:connectionId/:projectId/:tab?",
    layout: "/app",
    viewportCard: {
      header: {
        enableBackButton: true,
        // The STATIC title is the fallback; the project's NAME is rendered by
        // the header COMPONENT right after it (ClientDetail's pattern), since
        // routeDisplayTitle reads this string for the omnibar / nav.
        title: "Project",
        enableRefreshButton: true,
        component: () => import("./DevOpsProject.Header"),
      },
      body: {
        component: () => import("./DevOpsProject.Body"),
        grayBackground: true,
      },
    },
    group: "DevOps",
    hidden: true,
    // Reads are viewer+ (the API's staff-read gate on `devops-overview`); the
    // writes here — link, subscribe, edit, remove — are operator+, hidden
    // below that tier and re-checked by the API.
    requiresAuth: true,
    requiresPermission: false,
    onEnter: ({ rootStore, params, queryParams, manualTrigger }) => {
      const store = rootStore.devopsProjectStore as DevOpsProjectStore | undefined;
      const identity = identityOf(params);
      if (!store || !identity) return;
      store.setTarget(identity.connectionId, identity.projectId);
      // The header's refresh re-runs this hook with `manualTrigger` — then
      // the open board tab reloads too, not only the overview.
      if (manualTrigger) {
        void store.refresh(tabOf(queryParams));
      } else {
        void store.load();
        void store.syncTab(tabOf(queryParams));
      }
    },
    // Guarded on IDENTITY for the reload: a tab switch rewrites the query
    // string and fires this hook, and must prime the board behind the tab
    // without refetching the whole page.
    onParamsChange: ({ rootStore, params, queryParams }) => {
      const store = rootStore.devopsProjectStore as DevOpsProjectStore | undefined;
      const identity = identityOf(params);
      if (!store || !identity) return;
      if (store.connectionId !== identity.connectionId || store.projectId !== identity.projectId) {
        store.setTarget(identity.connectionId, identity.projectId);
        void store.load();
      }
      void store.syncTab(tabOf(queryParams));
    },
    // Tear down the live feeds of the boards this page primed.
    onExit: ({ rootStore }) => {
      const store = rootStore.devopsProjectStore as DevOpsProjectStore | undefined;
      store?.dispose();
    },
    // The shared "Link a project" dialog — one definition, spread per route
    // because dialogs are registered per route in this router.
    dialogs: [devopsLinkDialog],
  },
];

export default devopsProjectRoute;
